import clsx from 'clsx';
import { useField, useFormikContext } from 'formik';
import type { ChangeEvent, FC } from 'react';
import { Fragment, useRef, useState } from 'react';
import { MdOutlineImage } from 'react-icons/md/index';
import UploaderBody from './components/UploaderBody';
import { isFacebookApp, uploadFile } from './utils';
import InputError from '~/components/feedback/InputError';
import config from '~/config';
import { useReCAPTCHAComponent } from '~/hooks/useReCAPTCHAComponent';
import type { IFileInput } from '~/types/inputs';
import { getApiURL } from '~/utils/api';
import { getInputSizeClasses } from '~/utils/form';
import './style.css';

import ReceiptExampleModal from './components/ReceiptExampleModal';
import { linkUnderlineClasses } from '~/utils';

export type FileInputState = 'ready' | 'uploading' | 'uploaded' | 'error';

const File: FC<IFileInput & { promoSlug: string }> = ({
	name,
	label,
	width,
	required,
	promoSlug,
}) => {
	const inputRef = useRef(null);
	const [field, meta, helpers] = useField(name);
	const [isReceiptExampleModalOpen, setIsReceiptExampleModalOpen] = useState(false);
	const [state, setState] = useState<FileInputState>('ready');
	const { submitCount } = useFormikContext();
	const isFormikError = (meta.touched || submitCount > 0) && !!meta.error;
	const [errorMessage, setErrorMessage] = useState('');
	const [fileSize, setFileSize] = useState(0);
	const [fileName, setFileName] = useState('');
	const [uploadProgress, setUploadProgress] = useState(0);
	const recaptchaRef = useRef<any>();
	const ReCAPTCHA = useReCAPTCHAComponent();

	/**
	 * When opened through the embedded browser in the
	 * Facebook app, the `accept` property on the input makes users unable to upload
	 */
	const isAcceptSupported = !isFacebookApp();

	const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
		event.preventDefault();
		helpers.setTouched(true);

		setState('uploading');
		setErrorMessage('');
		setFileName('');
		setFileSize(0);

		const files = event.target.files;
		const file = files[0];

		setFileSize(file.size);
		setFileName(file.name);

		if (!file) {
			setState('error');
			setErrorMessage('File upload failed. Please try again');
			return;
		}

		if (!recaptchaRef.current) {
			setState('error');
			setErrorMessage('Failed to validate request. Please try again');
			return;
		}

		// 5 megabyte upper limit
		if (file.size > 5000000) {
			setState('error');
			setErrorMessage('The size limit for files is 5MB, please upload a smaller file.');
			return;
		}

		if (!['image/jpeg', 'image/png', 'application/pdf'].includes(file.type)) {
			setState('error');
			setErrorMessage(
				'Please upload a file in a valid format. Accepted formats are .jpeg, .png, and .pdf',
			);
			return;
		}

		try {
			const token = await recaptchaRef.current.executeAsync();
			setState('uploading');
			const receiptURLRequest = await fetch(`${getApiURL(promoSlug)}/receipt_upload_url`, {
				body: JSON.stringify({
					name: file.name,
					type: file.type,
					recaptcha: token,
				}),
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
			});
			const {
				data: { signedURL, objectURL },
			} = await receiptURLRequest.json();

			await uploadFile(signedURL, file, setUploadProgress);
			helpers.setValue(objectURL);
			setState('uploaded');
		} catch (e) {
			console.log(e);
			setState('error');
		}
	};

	const reset = () => {
		setState('ready');
		setFileSize(0);
		setUploadProgress(0);
		setFileName('');
		if (inputRef.current) {
			inputRef.current.value = '';
		}
		helpers.setValue('');
	};

	const handleHelpButtonPressed = () => {
		setIsReceiptExampleModalOpen(!isReceiptExampleModalOpen);
	};

	return (
		<Fragment>
			<div className={clsx(getInputSizeClasses(width))}>
				{ReCAPTCHA && (
					<ReCAPTCHA
						sitekey={config.recaptchaSiteKey}
						size="invisible"
						ref={recaptchaRef}
						className="invisible pointer-events-none"
					/>
				)}
				<div className="flex justify-between px-5 mb-2 items-center">
					<label
						htmlFor={name}
						className="file-input-label relative font-large-body flex items-center cursor-pointer"
					>
						{label}
						{required && !label.endsWith('*') ? <sup>*</sup> : null}
					</label>
					{config.useReceiptExample && <ReceiptExampleModal />}
				</div>
				<div
					className={clsx(
						'file-input-container relative flex items-center justify-center h-file-input rounded-file-input py-6 group border-2 border-dashed transition-colors duration-200',
						state === 'error' ? 'is-error' : '',
					)}
				>
					<UploaderBody
						fileName={fileName}
						fileSize={fileSize}
						state={state}
						uploadProgress={uploadProgress}
						errorMessage={errorMessage}
						onReset={reset}
					>
						<input
							{...field}
							ref={inputRef}
							onChange={handleUpload}
							accept={isAcceptSupported ? '.jpg,.jpeg,.png,.pdf' : ''}
							type="file"
							className="absolute inset-0 opacity-0 cursor-pointer file-upload"
							id={name}
						/>
						<span className="inner-label relative z-10 font-large-body flex text-sm items-center cursor-pointer pointer-events-none text-center">
							<MdOutlineImage className="w-[24px] h-[24px] mr-1" />
							<span className="pseudo-link block">
								Drop your image or{' '}
								<span
									className={clsx(
										'file-input-browse cursor-pointer',
										linkUnderlineClasses,
									)}
								>
									choose file
								</span>
							</span>
						</span>
					</UploaderBody>
					<InputError name={name} isError={isFormikError}>
						{meta.error}
					</InputError>
				</div>
			</div>
		</Fragment>
	);
};

export default File;
